import { useRef, useState } from "react";
import { api } from "../lib/api";
import { formatDateTime, formatINR } from "../lib/format";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

export function todayIST() {
    return new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);
}

/** Advisory duplicate check. `guard(params, proceed)` runs proceed directly or after "Continue Anyway". */
export function useDuplicateGuard() {
    const [dups, setDups] = useState(null);
    const pending = useRef(null);

    async function guard(params, proceed) {
        let found = [];
        try {
            const r = await api.get("/collections/duplicate-check", { params });
            found = r.data?.duplicates || [];
        } catch {
            found = [];
        }
        if (!found.length) return proceed();
        pending.current = proceed;
        setDups(found);
    }

    const dialog = (
        <AlertDialog open={!!dups} onOpenChange={(o) => !o && setDups(null)}>
            <AlertDialogContent data-testid="duplicate-warning-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle>Possible duplicate entry</AlertDialogTitle>
                    <AlertDialogDescription>
                        An entry with the same amount and collection date already exists for this account.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                    {(dups || []).map((d, i) => (
                        <div key={i} className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm" data-testid={`duplicate-item-${i}`}>
                            <div className="flex justify-between font-medium text-slate-900">
                                <span>{formatINR(d.amount || 0)}</span>
                                <span className="capitalize text-amber-700">{d.status}</span>
                            </div>
                            <div className="text-slate-600 mt-1">{formatDateTime(d.collection_datetime)}</div>
                            <div className="text-slate-600">Entered by: {d.entered_by || "—"}{d.ref ? ` · ${d.ref}` : ""}</div>
                        </div>
                    ))}
                </div>
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="duplicate-cancel-btn" onClick={() => { pending.current = null; }}>Cancel</AlertDialogCancel>
                    <AlertDialogAction data-testid="duplicate-continue-btn" className="bg-blue-900 hover:bg-blue-800 text-white"
                        onClick={() => { const fn = pending.current; pending.current = null; setDups(null); fn && fn(); }}>
                        Continue Anyway
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
    return { guard, dialog };
}
